import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { getUnitsByLocationId } from '@core/booking/booking.service';
import { Location, Unit } from '@core/booking/booking.types';
import { useLocationsStore } from '@core/locations/store/locations';

import { CityGroup, LocationWithRooms } from './meeting-room-landing.types';

/**
 * Extracts city name from an address string.
 * Walks backwards through comma-separated parts, skipping:
 *  - Country codes (US, UK, CA, UAE, etc.)
 *  - US state + ZIP (e.g. "MI 48858", "CA 90210-1234")
 *  - Standalone postal/ZIP codes
 *  - Street lines (start with a number, e.g. "1234 Main St")
 * The first remaining part is treated as the city.
 */
function extractCityFromAddress(address: string): string {
  if (!address) return 'Other';
  const parts = address
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) return parts[0] || 'Other';

  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    // Country codes / short country names (US, UK, CA, UAE, etc.)
    if (/^[A-Z]{2,3}$/i.test(part)) continue;
    // US state + ZIP: "MI 48858", "NY 10001-1234"
    if (/^[A-Z]{2}\s+\d{4,5}(-\d{4})?$/i.test(part)) continue;
    // Canadian province + postal: "ON M5V 2T6"
    if (/^[A-Z]{2}\s+[A-Z]\d[A-Z]\s*\d[A-Z]\d$/i.test(part)) continue;
    // Standalone ZIP / postal codes: "48858", "10001-1234", "EC1A 1BB"
    if (/^\d{4,6}(-\d{4})?$/.test(part)) continue;
    if (/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(part)) continue;
    // Street address lines (start with a number)
    if (/^\d+\s/.test(part)) continue;
    return part;
  }

  return parts[parts.length - 2] || parts[0];
}

function groupByCity(locationsWithRooms: LocationWithRooms[]): CityGroup[] {
  const cityMap = new Map<string, LocationWithRooms[]>();
  for (const loc of locationsWithRooms) {
    const existing = cityMap.get(loc.city) || [];
    existing.push(loc);
    cityMap.set(loc.city, existing);
  }
  return Array.from(cityMap.entries()).map(([city, locations]) => ({ city, locations }));
}

export const useMeetingRoomLanding = () => {
  const { query } = useRouter();
  const phpURL = useMemo(() => {
    return `https://${query.clientName}.pickspace.com/api/app`;
  }, [query.clientName]);

  const { locations } = useLocationsStore();

  const [loading, setLoading] = useState(true);
  const [cityGroups, setCityGroups] = useState<CityGroup[]>([]);

  useEffect(() => {
    if (!locations.length || !query.clientName) return;

    setLoading(true);

    const fetchAllRooms = async () => {
      const results = await Promise.allSettled(
        locations.map(async (location) => {
          const rooms: Unit[] = await getUnitsByLocationId(phpURL, location.id);
          return { location, rooms: Array.isArray(rooms) ? rooms : [] };
        }),
      );

      const withRooms: LocationWithRooms[] = results
        .filter(
          (r): r is PromiseFulfilledResult<{ location: Location; rooms: Unit[] }> =>
            r.status === 'fulfilled' && r.value.rooms.length > 0,
        )
        .map((r) => ({
          location: r.value.location,
          city: r.value.location.city || extractCityFromAddress(r.value.location.address),
          rooms: r.value.rooms,
        }));

      setCityGroups(groupByCity(withRooms));
      setLoading(false);
    };

    fetchAllRooms();
  }, [locations, phpURL, query.clientName]);

  return { loading, cityGroups };
};
